// Shared channel classifier: CHANNEL_SPEC v2026-10-10.4. The order webhook and
// the attribution report run the same rules; the reference implementation is
// tools/attribution/channel.py in the ANATOMY workspace, and it wins where the
// two disagree. The classifier is a pure function of the touch it is given and
// stores nothing, so it is exposed before consent; callers pass only data they
// are allowed to hold.
(function () {
  'use strict';

  if (window.AnatomyChannel && window.AnatomyChannel.specVersion) return;

  var SPEC_VERSION = '2026-10-10.4';
  var hasOwn = Object.prototype.hasOwnProperty;

  function setOf(values) {
    var set = Object.create(null);
    values.forEach(function (value) { set[value] = true; });
    return set;
  }

  var SOURCE_ALIASES = {
    'meta': 'meta', 'facebook': 'meta', 'fb': 'meta', 'ig': 'meta', 'instagram': 'meta',
    'meta-sitelink': 'meta', 'an': 'meta', 'msg': 'meta', 'threads': 'meta',
    'google': 'google', 'google-ads': 'google', 'googleads': 'google', 'adwords': 'google', 'youtube': 'google',
    'bing': 'bing', 'microsoft': 'bing',
    'klaviyo': 'klaviyo', 'email': 'klaviyo', 'newsletter': 'klaviyo',
    'linkby': 'linkby',
    'awin': 'awin', 'shareasale': 'awin',
    'chatgpt': 'chatgpt', 'chatgpt.com': 'chatgpt', 'openai': 'chatgpt',
    'perplexity': 'perplexity', 'perplexity.ai': 'perplexity',
    'gemini': 'gemini', 'bard': 'gemini',
    'claude': 'claude', 'claude.ai': 'claude',
    'copilot': 'copilot',
    'tiktok': 'tiktok',
    'pinterest': 'pinterest',
    'reddit': 'reddit',
    'shop_app': 'shop_app', 'shop': 'shop_app', 'shopapp': 'shop_app', 'shop-website': 'shop_app',
    'brandreward': 'brandreward'
  };
  var AFFILIATE_PLATFORMS = setOf(['linkby', 'awin', 'brandreward', 'rakuten', 'cj', 'impact', 'skimlinks',
    'sovrn', 'partnerize']);
  // Affiliate-network redirect hosts: a referrer from one of these is an affiliate click even without UTMs.
  var AFFILIATE_HOSTS = [
    ['shareasale-analytics.com', 'awin'], ['shareasale.com', 'awin'], ['awin1.com', 'awin'],
    ['brandreward.com', 'brandreward'], ['linksynergy.com', 'rakuten'], ['anrdoezrs.net', 'cj'],
    ['dpbolvw.net', 'cj'], ['jdoqocy.com', 'cj'], ['tkqlhce.com', 'cj'], ['kqzyfj.com', 'cj'],
    ['emjcd.com', 'cj'], ['sjv.io', 'impact'], ['pxf.io', 'impact'], ['7eer.net', 'impact'],
    ['ojrq.net', 'impact'], ['evyy.net', 'impact'], ['skimresources.com', 'skimlinks'],
    ['viglink.com', 'sovrn'], ['sovrn.co', 'sovrn'], ['prf.hn', 'partnerize']
  ];
  // Coupon, deal and cash-back sites: usually visited just before buying, to find a code.
  var COUPON_HOSTS = ['simplycodes.com', 'dealspotr.com', 'fatcoupon.com', 'couponchief.com', 'trustdeals.com',
    'discountreactor.com', 'retailmenot.com', 'joinhoney.com', 'capitaloneshopping.com', 'knoji.com',
    'couponbirds.com', 'wethrift.com', 'dealdrop.com', 'coupons.com', 'slickdeals.net',
    'couponfollow.com', 'dontpayfull.com', 'promocodes.com', 'karmanow.com', 'rakuten.com'];
  var OWN_HOSTS = ['anatomyhaircare.com', 'anatomyhaircare.myshopify.com', 'checkout.shopify.com'];
  // Payment and sign-in hops: a return from these is part of checkout, never a traffic source.
  var PAYMENT_HOSTS = ['pay.shopify.com', 'shopify.com', 'amazon.com', 'paypal.com', 'klarna.com',
    'afterpay.com', 'affirm.com', 'sezzle.com', 'stripe.com'];
  var EMAIL_HOSTS = ['mail.google.com', 'outlook.live.com', 'outlook.office.com', 'mail.yahoo.com', 'klaviyo'];
  var META_HOST_PARTS = ['facebook', 'fb.com', 'instagram', 'messenger.com', 'threads.net'];
  // Ordered: the first substring found in the referrer host wins.
  var REFERRER_HOST_PARTS = [
    ['bing.com', 'bing'], ['bing', 'bing'], ['duckduckgo', 'duckduckgo'], ['yahoo', 'yahoo'],
    ['ecosia', 'ecosia'], ['search.brave.com', 'brave'], ['yandex', 'yandex'], ['baidu', 'baidu'],
    ['startpage', 'startpage'], ['qwant', 'qwant'], ['aol', 'aol'], ['ask.com', 'ask'],
    ['naver', 'naver'],
    ['chatgpt.com', 'chatgpt'], ['chat.openai.com', 'chatgpt'], ['perplexity', 'perplexity'],
    ['claude.ai', 'claude'], ['copilot.microsoft.com', 'copilot'], ['tiktok', 'tiktok'],
    ['pinterest', 'pinterest'], ['reddit', 'reddit'], ['redd.it', 'reddit'], ['shop.app', 'shop_app']
  ];
  var NO_REFERRER_LABELS = setOf(['an unknown source', 'unknown', 'direct', '(direct)', 'none']);
  var PAID = setOf(['paid', 'paid-social', 'paid_social', 'paidsocial', 'social-paid', 'cpc', 'ppc', 'cpm',
    'cpv', 'display', 'paid-search', 'paid_search', 'sem', 'ads', 'ad']);
  var ORGANIC = setOf(['social', 'organic', 'organic-social', 'organic_social', 'bio', 'link_in_bio',
    'profile', 'post', 'story', 'stories', 'share', 'social_share']);
  var EMAIL = setOf(['email', 'e-mail', 'newsletter', 'sms', 'flow']);
  var AFFILIATE = setOf(['affiliate', 'partner', 'influencer', 'creator']);
  var AI = setOf(['chatgpt', 'perplexity', 'gemini', 'claude', 'copilot']);
  var SEARCH = setOf(['google', 'bing', 'duckduckgo', 'yahoo', 'ecosia', 'brave', 'yandex', 'baidu',
    'startpage', 'qwant', 'aol', 'ask', 'naver']);
  var CLICK_KEYS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'gad_source', 'ttclid', 'msclkid', 'rdt_cid'];
  var SIGNAL_KEYS = ['srsltid', '_kx', 'awc', 'sscid', 'sv1', 'lbclid', 'lb_hash'];
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  // Checkout, payment-callback, sign-in and order-status URLs: re-entries, not acquisition touches.
  var REENTRY_PATH = /^\/(checkouts?|cart\/c|customer_authentication|account|orders|[0-9]+\/(orders|checkouts))(\/|$)|shop_pay_callback/;
  var REENTRY_PARAMS = ['shop_sign_in', 'checkout_queue_token', 'shop_consented_scopes'];
  // Schemes whose last path segment carries ";params" (Python's urllib.parse.uses_params).
  var USES_PARAMS = setOf(['', 'ftp', 'hdl', 'prospero', 'http', 'imap', 'https', 'shttp', 'rtsp', 'rtsps',
    'rtspu', 'sip', 'sips', 'mms', 'sftp', 'tel']);
  // The fields of a stored touch (first_* / last_*) the classifier reads.
  var TOUCH_FIELDS = UTM_KEYS.concat(['referrer', 'landing_url', 'fbclid', 'gclid', 'gbraid', 'wbraid',
    'ttclid', 'msclkid']);

  function text(value) {
    return value ? String(value) : '';
  }

  // urllib.parse.unquote: malformed escapes stay literal and invalid UTF-8
  // becomes U+FFFD, where decodeURIComponent would throw.
  function unquote(value) {
    if (value.indexOf('%') === -1) return value;
    try {
      return decodeURIComponent(value);
    }
    catch (error) {}
    if (typeof TextDecoder !== 'function') return value;
    return value.replace(/[\u0000-\u007f]+/g, function (run) {
      if (run.indexOf('%') === -1) return run;
      var bytes = [];
      for (var index = 0; index < run.length; index += 1) {
        var hex = run.substr(index + 1, 2);
        if (run.charAt(index) === '%' && /^[0-9a-f]{2}$/i.test(hex)) {
          bytes.push(parseInt(hex, 16));
          index += 2;
        }
        else {
          bytes.push(run.charCodeAt(index));
        }
      }
      return new TextDecoder('utf-8').decode(new Uint8Array(bytes));
    });
  }

  // urllib.parse.urlparse, reduced to the parts the rules use, so that the
  // storefront reads a URL exactly as the webhook and the report do.
  function urlParts(url) {
    var rest = String(url || '').replace(/^[\u0000- ]+/, '').replace(/[\t\r\n]/g, '');
    var scheme = '';
    var colon = rest.indexOf(':');
    if (colon > 0 && /^[a-z][a-z0-9+.\-]*$/i.test(rest.slice(0, colon))) {
      scheme = rest.slice(0, colon).toLowerCase();
      rest = rest.slice(colon + 1);
    }
    var netloc = '';
    if (rest.slice(0, 2) === '//') {
      var end = rest.length;
      ['/', '?', '#'].forEach(function (delimiter) {
        var at = rest.indexOf(delimiter, 2);
        if (at !== -1 && at < end) end = at;
      });
      netloc = rest.slice(2, end);
      rest = rest.slice(end);
      if ((netloc.indexOf('[') === -1) !== (netloc.indexOf(']') === -1)) return { error: true };
    }
    var hashAt = rest.indexOf('#');
    if (hashAt !== -1) rest = rest.slice(0, hashAt);
    var query = '';
    var queryAt = rest.indexOf('?');
    if (queryAt !== -1) {
      query = rest.slice(queryAt + 1);
      rest = rest.slice(0, queryAt);
    }
    var path = rest;
    if (USES_PARAMS[scheme] && path.indexOf(';') !== -1) {
      var semicolon = path.indexOf('/') !== -1 ? path.indexOf(';', path.lastIndexOf('/')) : path.indexOf(';');
      if (semicolon !== -1) path = path.slice(0, semicolon);
    }
    var hostinfo = netloc.slice(netloc.lastIndexOf('@') + 1);
    var hostname;
    var bracket = hostinfo.indexOf('[');
    if (bracket !== -1) {
      hostname = hostinfo.slice(bracket + 1);
      if (hostname.indexOf(']') !== -1) hostname = hostname.slice(0, hostname.indexOf(']'));
    }
    else {
      hostname = hostinfo.split(':')[0];
    }
    return { error: false, hostname: hostname.toLowerCase(), path: path, query: query };
  }

  // urllib.parse.parse_qs(keep_blank_values=True): the first value of each
  // name, names lower-cased.
  function parseQuery(query) {
    var firstValues = Object.create(null);
    var names = [];
    (query ? query.split('&') : []).forEach(function (pair) {
      if (!pair) return;
      var equals = pair.indexOf('=');
      var name = unquote((equals === -1 ? pair : pair.slice(0, equals)).replace(/\+/g, ' '));
      var value = unquote((equals === -1 ? '' : pair.slice(equals + 1)).replace(/\+/g, ' '));
      if (name in firstValues) return;
      firstValues[name] = value;
      names.push(name);
    });
    var params = Object.create(null);
    names.forEach(function (name) {
      params[name.toLowerCase()] = firstValues[name];
    });
    return params;
  }

  function hostOf(referrer) {
    var value = text(referrer).trim().toLowerCase();
    if (!value) return '';
    var host;
    if (value.indexOf('://') !== -1 || value.indexOf('//') === 0) {
      var parts = urlParts(value.indexOf('://') !== -1 ? value : 'https:' + value);
      host = parts.error ? '' : parts.hostname;
    }
    else {
      host = value.split('/')[0]; // a bare host, or a Shopify label such as "instagram"
    }
    return host.replace(/^(www\.|m\.)/, '');
  }

  function hostIn(host, domains) {
    return domains.some(function (domain) {
      return host === domain || host.slice(-(domain.length + 1)) === '.' + domain;
    });
  }

  function referrerPlatform(host) {
    if (!host) return '';
    if (hostIn(host, OWN_HOSTS) || hostIn(host, PAYMENT_HOSTS)) return 'own';
    if (host === 'email' || EMAIL_HOSTS.some(function (part) { return host.indexOf(part) !== -1; })) return 'email';
    if (host === 'gemini.google.com') return 'gemini';
    if (META_HOST_PARTS.some(function (part) { return host.indexOf(part) !== -1; })) return 'meta';
    if (host === 'google' || /^([a-z0-9-]+\.)*google\.[a-z.]+$/.test(host)) return 'google';
    for (var index = 0; index < REFERRER_HOST_PARTS.length; index += 1) {
      if (host.indexOf(REFERRER_HOST_PARTS[index][0]) !== -1) return REFERRER_HOST_PARTS[index][1];
    }
    for (var affiliateIndex = 0; affiliateIndex < AFFILIATE_HOSTS.length; affiliateIndex += 1) {
      if (hostIn(host, [AFFILIATE_HOSTS[affiliateIndex][0]])) return AFFILIATE_HOSTS[affiliateIndex][1];
    }
    for (var couponIndex = 0; couponIndex < COUPON_HOSTS.length; couponIndex += 1) {
      if (hostIn(host, [COUPON_HOSTS[couponIndex]])) return 'coupon:' + COUPON_HOSTS[couponIndex];
    }
    if (NO_REFERRER_LABELS[host]) return '';
    return 'referral:' + host;
  }

  function isReentry(landingUrl) {
    var parts = urlParts(landingUrl);
    if (parts.error) return false;
    var params = parseQuery(parts.query);
    return REENTRY_PATH.test(parts.path.toLowerCase())
      || REENTRY_PARAMS.some(function (key) { return key in params; });
  }

  // touch: { utm_source, utm_medium, utm_campaign, utm_content, utm_term,
  // referrer, landing_url, fbclid, gclid, gbraid, wbraid, gad_source, ttclid,
  // msclkid }. UTMs, click ids and signals missing from the touch are read from
  // landing_url. Returns { platform, channel, is_paid, reentry }.
  function classify(input) {
    var touch = {};
    if (input && typeof input === 'object') {
      Object.keys(input).forEach(function (key) { touch[key] = input[key]; });
    }
    var landing = text(touch.landing_url);
    var params = Object.create(null);
    if (landing) {
      var parts = urlParts(landing);
      if (!parts.error) params = parseQuery(parts.query);
    }
    var reentry = !!landing && isReentry(landing);
    if (reentry) {
      // A checkout / sign-in return carries no acquisition signal of its own.
      var kept = {};
      Object.keys(touch).forEach(function (key) {
        if (UTM_KEYS.indexOf(key) !== -1 || CLICK_KEYS.indexOf(key) !== -1) kept[key] = touch[key];
      });
      touch = kept;
      params = Object.create(null);
    }
    var get = function (key) {
      var value = hasOwn.call(touch, key) ? touch[key] : '';
      return value || params[key] || '';
    };
    var source = text(get('utm_source')).trim().toLowerCase();
    var medium = text(get('utm_medium')).trim().toLowerCase();
    var macroUnresolved = ['utm_content', 'utm_campaign', 'utm_term'].some(function (key) {
      return text(get(key)).indexOf('{{') !== -1;
    });
    var clicks = {};
    CLICK_KEYS.forEach(function (key) { clicks[key] = !!get(key); });
    var signals = {};
    SIGNAL_KEYS.forEach(function (key) { signals[key] = !!get(key); });
    var referrerPlatformValue = reentry ? '' : referrerPlatform(hostOf(touch.referrer));
    if (referrerPlatformValue === 'own') referrerPlatformValue = '';
    var alias = source ? (hasOwn.call(SOURCE_ALIASES, source) ? SOURCE_ALIASES[source] : source) : '';
    var googlePaidClick = clicks.gclid || clicks.gbraid || clicks.wbraid || clicks.gad_source;
    var clickPlatform = clicks.fbclid ? 'meta'
      : googlePaidClick ? 'google'
        : clicks.ttclid ? 'tiktok'
          : clicks.msclkid ? 'bing'
            : clicks.rdt_cid ? 'reddit'
              : '';
    var signalPlatform = (signals.awc || signals.sscid || text(get('sv1')).toLowerCase() === 'affiliate') ? 'awin'
      : (signals.lbclid || signals.lb_hash) ? 'linkby'
        : signals._kx ? 'klaviyo'
          : signals.srsltid ? 'google'
            : '';
    var platform = alias || clickPlatform || signalPlatform || referrerPlatformValue || 'direct';

    var channel;
    if (platform === 'email' || platform === 'klaviyo' || EMAIL[medium]) {
      channel = 'email';
    }
    else if (AFFILIATE_PLATFORMS[platform] || AFFILIATE[medium]) {
      channel = 'affiliate';
    }
    else if (AI[platform]) {
      channel = 'ai_assistant';
    }
    else if (platform === 'meta' || platform === 'tiktok' || platform === 'pinterest' || platform === 'reddit') {
      var paidClick = platform === 'meta' ? clicks.fbclid : platform === 'tiktok' ? clicks.ttclid
        : platform === 'reddit' ? clicks.rdt_cid : false;
      if (ORGANIC[medium] || macroUnresolved) channel = 'organic_social';
      else if (paidClick || PAID[medium]) channel = 'paid_social';
      else channel = 'organic_social';
    }
    else if (SEARCH[platform]) {
      channel = googlePaidClick || clicks.msclkid || PAID[medium] ? 'paid_search' : 'organic_search';
    }
    else if (platform === 'shop_app') {
      channel = 'shop_app';
    }
    else if (platform.indexOf('coupon:') === 0) {
      channel = 'coupon';
    }
    else if (platform === 'direct') {
      channel = 'direct';
    }
    else {
      channel = 'referral';
    }
    return {
      platform: platform,
      channel: channel,
      is_paid: channel === 'paid_social' || channel === 'paid_search',
      reentry: reentry
    };
  }

  // The pre-classifier normalized_*_source vocabulary.
  function legacySource(result) {
    var platform = result && result.platform ? String(result.platform) : 'direct';
    if (platform === 'direct') return 'direct_or_unknown';
    if (platform.indexOf('referral:') === 0) return 'referral';
    return platform.indexOf('coupon:') === 0 ? 'coupon' : platform;
  }

  // A classifier touch from stored first_* / last_* attribution fields.
  function touchFromFields(values, prefix) {
    var touch = {};
    TOUCH_FIELDS.forEach(function (field) {
      var value = values ? values[prefix + '_' + field] : '';
      if (value) touch[field] = value;
    });
    return touch;
  }

  // True for our own storefront and for payment / sign-in hosts: a referrer
  // from one of them is a return from checkout, not a new touch.
  function isIgnoredReferrer(referrer) {
    return referrerPlatform(hostOf(referrer)) === 'own';
  }

  window.AnatomyChannel = {
    specVersion: SPEC_VERSION,
    classify: classify,
    classifyTouch: function (values, prefix) {
      return classify(touchFromFields(values, prefix));
    },
    legacySource: legacySource,
    touchFromFields: touchFromFields,
    isReentry: isReentry,
    isIgnoredReferrer: isIgnoredReferrer
  };
})();

(function () {
  'use strict';

  if (window.__ANATOMY_ATTRIBUTION_LOADED__) return;
  window.__ANATOMY_ATTRIBUTION_LOADED__ = true;

  var VERSION = '2026-09-25.2-channel-tracking';
  var STORAGE_KEY = 'anatomy_attribution_v1';
  var SESSION_KEY = 'anatomy_attribution_session_v1';
  var COOKIE_FIRST = 'an_attr_first';
  var COOKIE_LAST = 'an_attr_last';
  var COOKIE_CONTEXT = 'an_attr_context';
  var COOKIE_DAYS = 90;
  var DEBUG = /(?:[?&])attribution_debug=1(?:&|$)/.test(window.location.search);
  var STALE_KLAVIYO_FORM_ID = 'SsJJje';
  var ACTIVE_KLAVIYO_FORM_ID = 'Wsvzar';

  var channel = window.AnatomyChannel;

  function repairStaleKlaviyoForm() {
    if (!document || !document.querySelectorAll) return;

    var staleClass = 'klaviyo-form-' + STALE_KLAVIYO_FORM_ID;
    var activeClass = 'klaviyo-form-' + ACTIVE_KLAVIYO_FORM_ID;
    var changed = false;

    document.querySelectorAll('[data-klaviyo-form-id="' + STALE_KLAVIYO_FORM_ID + '"]').forEach(function (node) {
      node.setAttribute('data-klaviyo-form-id', ACTIVE_KLAVIYO_FORM_ID);
      changed = true;
    });

    document.querySelectorAll('.' + staleClass).forEach(function (node) {
      node.classList.remove(staleClass);
      node.classList.add(activeClass);
      changed = true;
    });

    if (changed && DEBUG && window.console && window.console.info) {
      window.console.info('[ANATOMY attribution] repaired stale Klaviyo form embed', {
        from: STALE_KLAVIYO_FORM_ID,
        to: ACTIVE_KLAVIYO_FORM_ID,
        path: window.location.pathname
      });
    }
  }

  var UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_placement',
    'utm_site',
    'utm_id',
    'utm_adset_id',
    'utm_ad_id',
    'utm_adset',
    'utm_ad',
    'adset_id',
    'ad_group_id',
    'adgroup_id',
    'ad_id',
    'creative_id',
    'campaign_id',
    'fb_adset_id',
    'fb_ad_id',
    'fbadid',
    'tt_adgroup_id',
    'tt_ad_id',
    // Awin's affiliate marker (sv1=affiliate): a campaign tag, not an identifier.
    'sv1'
  ];

  // Advertising identifiers: stored and kept in landing URLs only with
  // advertising consent. awc/sscid (Awin), lbclid/lb_hash (Linkby) and srsltid
  // (Google merchant listings) are channel signals the classifier reads.
  var CLICK_ID_KEYS = [
    'fbclid', 'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', 'gad_source',
    'ttclid', 'twclid', 'li_fat_id', 'igshid', 'rdt_cid', 'qclid', 'sccid',
    'msclkid', 'epik', 'irclid', '_kx', 'mc_cid', 'mc_eid',
    'awc', 'sscid', 'lbclid', 'lb_hash', 'srsltid'
  ];
  var FIRST_FIELDS = [
    'first_landing_url',
    'first_landing_path',
    'first_referrer',
    'first_utm_source',
    'first_utm_medium',
    'first_utm_campaign',
    'first_utm_content',
    'first_utm_term',
    'first_utm_placement',
    'first_utm_site',
    'first_utm_id',
    'first_utm_adset_id',
    'first_utm_ad_id',
    'first_fbclid',
    'first_gclid',
    'first_gbraid',
    'first_wbraid',
    'first_ttclid',
    'first_msclkid',
    'first_timestamp'
  ];
  var LAST_FIELDS = [
    'last_landing_url',
    'last_landing_path',
    'last_referrer',
    'last_utm_source',
    'last_utm_medium',
    'last_utm_campaign',
    'last_utm_content',
    'last_utm_term',
    'last_utm_placement',
    'last_utm_site',
    'last_utm_id',
    'last_utm_adset_id',
    'last_utm_ad_id',
    'last_fbclid',
    'last_gclid',
    'last_gbraid',
    'last_wbraid',
    'last_ttclid',
    'last_msclkid',
    'last_timestamp'
  ];
  var CONTEXT_FIELDS = [
    'attribution_session_entry_url',
    'attribution_session_entry_path',
    'attribution_session_referrer',
    'attribution_user_agent',
    'attribution_fbp',
    'attribution_fbc'
  ];
  var SENSITIVE_PARAMS = [
    'email',
    'e',
    'phone',
    'name',
    'first_name',
    'last_name',
    'address',
    'customer',
    'token',
    'password'
  ];

  var currentState = null;
  var lastLpCheckoutEventAt = 0;

  function truncate(value, maxLength) {
    if (value === null || typeof value === 'undefined') return '';
    var stringValue = String(value);
    if (stringValue.length <= maxLength) return stringValue;
    return stringValue.slice(0, maxLength - 3) + '...';
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function parseJson(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    }
    catch (error) {
      return null;
    }
  }

  function safeStorageGet(storage, key) {
    try {
      return storage.getItem(key);
    }
    catch (error) {
      return null;
    }
  }

  function safeStorageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    }
    catch (error) {
      return false;
    }
  }

  function safeStorageRemove(storage, key) {
    try {
      storage.removeItem(key);
    }
    catch (error) {}
  }

  function getCookie(name) {
    var pairs = document.cookie ? document.cookie.split('; ') : [];
    for (var index = 0; index < pairs.length; index += 1) {
      var pair = pairs[index].split('=');
      if (decodeURIComponent(pair[0]) === name) {
        return decodeURIComponent(pair.slice(1).join('=') || '');
      }
    }
    return '';
  }

  function setCookie(name, value) {
    var expires = new Date();
    expires.setTime(expires.getTime() + COOKIE_DAYS * 24 * 60 * 60 * 1000);
    var cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value)
      + '; expires=' + expires.toUTCString()
      + '; path=/; SameSite=Lax';
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function deleteCookie(name) {
    try {
      document.cookie = encodeURIComponent(name) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    }
    catch (error) {}
  }

  function safeUrl(input, maxLength, includeAdIdentifiers) {
    if (!input) return '';
    try {
      var url = new URL(input, window.location.origin);
      for (var index = 0; index < SENSITIVE_PARAMS.length; index += 1) {
        url.searchParams.delete(SENSITIVE_PARAMS[index]);
      }
      if (includeAdIdentifiers === false) {
        for (var clickIndex = 0; clickIndex < CLICK_ID_KEYS.length; clickIndex += 1) {
          url.searchParams.delete(CLICK_ID_KEYS[clickIndex]);
        }
      }
      return truncate(url.href, maxLength);
    }
    catch (error) {
      return includeAdIdentifiers === false ? '' : truncate(input, maxLength);
    }
  }

  function safePath(maxLength, includeAdIdentifiers, input) {
    try {
      var url = new URL(input || window.location.href, window.location.origin);
      for (var index = 0; index < SENSITIVE_PARAMS.length; index += 1) {
        url.searchParams.delete(SENSITIVE_PARAMS[index]);
      }
      if (includeAdIdentifiers === false) {
        for (var clickIndex = 0; clickIndex < CLICK_ID_KEYS.length; clickIndex += 1) {
          url.searchParams.delete(CLICK_ID_KEYS[clickIndex]);
        }
      }
      return truncate(url.pathname + url.search, maxLength);
    }
    catch (error) {
      return truncate(window.location.pathname, maxLength);
    }
  }

  // Our own storefront, and payment / sign-in hosts (pay.shopify.com, PayPal,
  // Klarna ...): coming back from one is part of checkout, not a new touch.
  function isOwnHost(hostname) {
    return channel.isIgnoredReferrer(String(hostname || ''));
  }

  function externalReferrer(includeAdIdentifiers) {
    var referrer = document.referrer || '';
    if (!referrer) return '';
    try {
      var hostname = new URL(referrer).hostname;
      if (isOwnHost(hostname)) return '';
      return safeUrl(referrer, 500, includeAdIdentifiers);
    }
    catch (error) {
      return includeAdIdentifiers === false ? '' : truncate(referrer, 500);
    }
  }

  function currentParams() {
    try {
      return new URLSearchParams(window.location.search);
    }
    catch (error) {
      return new URLSearchParams('');
    }
  }

  function paramValue(params, name, maxLength) {
    var value = params.get(name);
    return value ? truncate(value, maxLength || 200) : '';
  }

  function firstParamValue(params, names, maxLength) {
    for (var index = 0; index < names.length; index += 1) {
      var value = paramValue(params, names[index], maxLength);
      if (value) return value;
    }
    return '';
  }

  function hasParamValue(params, name) {
    return params.has(name) && !!params.get(name);
  }

  function hasMeaningfulSignal(params, referrer, includeAdIdentifiers) {
    var index;
    for (index = 0; index < UTM_KEYS.length; index += 1) {
      if (hasParamValue(params, UTM_KEYS[index])) return true;
    }
    if (includeAdIdentifiers) {
      for (index = 0; index < CLICK_ID_KEYS.length; index += 1) {
        if (hasParamValue(params, CLICK_ID_KEYS[index])) return true;
      }
    }
    return !!referrer;
  }

  function buildTouch(prefix, params, referrer, includeAdIdentifiers) {
    var data = {};
    data[prefix + '_landing_url'] = safeUrl(window.location.href, 700, includeAdIdentifiers);
    data[prefix + '_landing_path'] = safePath(350, includeAdIdentifiers);
    data[prefix + '_referrer'] = referrer;
    data[prefix + '_utm_source'] = paramValue(params, 'utm_source');
    data[prefix + '_utm_medium'] = paramValue(params, 'utm_medium');
    data[prefix + '_utm_campaign'] = paramValue(params, 'utm_campaign');
    data[prefix + '_utm_content'] = paramValue(params, 'utm_content');
    data[prefix + '_utm_term'] = paramValue(params, 'utm_term');
    data[prefix + '_utm_placement'] = paramValue(params, 'utm_placement');
    data[prefix + '_utm_site'] = paramValue(params, 'utm_site');
    data[prefix + '_utm_id'] = firstParamValue(params, ['utm_id', 'campaign_id']);
    data[prefix + '_utm_adset_id'] = firstParamValue(params, ['utm_adset_id', 'adset_id', 'ad_group_id', 'adgroup_id', 'fb_adset_id', 'tt_adgroup_id', 'utm_adset']);
    data[prefix + '_utm_ad_id'] = firstParamValue(params, ['utm_ad_id', 'ad_id', 'fb_ad_id', 'fbadid', 'tt_ad_id', 'creative_id', 'utm_ad']);
    if (includeAdIdentifiers) {
      data[prefix + '_fbclid'] = paramValue(params, 'fbclid', 300);
      data[prefix + '_gclid'] = paramValue(params, 'gclid', 300);
      data[prefix + '_gbraid'] = paramValue(params, 'gbraid', 300);
      data[prefix + '_wbraid'] = paramValue(params, 'wbraid', 300);
      data[prefix + '_ttclid'] = paramValue(params, 'ttclid', 300);
      data[prefix + '_msclkid'] = paramValue(params, 'msclkid', 300);
    }
    data[prefix + '_timestamp'] = nowIso();
    return data;
  }

  function touchEquals(left, right, fields) {
    if (!left || !right) return false;
    for (var index = 0; index < fields.length; index += 1) {
      var field = fields[index];
      if (field.indexOf('_timestamp') !== -1) continue;
      if ((left[field] || '') !== (right[field] || '')) return false;
    }
    return true;
  }

  function fillMissing(target, source, fields) {
    var changed = false;
    for (var index = 0; index < fields.length; index += 1) {
      var field = fields[index];
      if (!target[field] && source[field]) {
        target[field] = source[field];
        changed = true;
      }
    }
    return changed;
  }

  function loadState() {
    var stored = parseJson(safeStorageGet(window.localStorage, STORAGE_KEY));
    if (stored && typeof stored === 'object') return stored;

    var first = parseJson(getCookie(COOKIE_FIRST));
    var last = parseJson(getCookie(COOKIE_LAST));
    var context = parseJson(getCookie(COOKIE_CONTEXT));
    return {
      version: VERSION,
      first: first || null,
      last: last || null,
      context: context || {}
    };
  }

  function loadSession(params, referrer, includeAdIdentifiers) {
    var session = parseJson(safeStorageGet(window.sessionStorage, SESSION_KEY));
    if (session && typeof session === 'object') {
      if (includeAdIdentifiers === false) {
        session.attribution_session_entry_url = safeUrl(session.attribution_session_entry_url, 700, false);
        session.attribution_session_entry_path = safePath(350, false, session.attribution_session_entry_path);
        session.attribution_session_referrer = safeUrl(session.attribution_session_referrer, 500, false);
        safeStorageSet(window.sessionStorage, SESSION_KEY, JSON.stringify(session));
      }
      return session;
    }

    session = {
      attribution_session_entry_url: safeUrl(window.location.href, 700, includeAdIdentifiers),
      attribution_session_entry_path: safePath(350, includeAdIdentifiers),
      attribution_session_referrer: referrer,
      started_at: nowIso()
    };
    safeStorageSet(window.sessionStorage, SESSION_KEY, JSON.stringify(session));
    return session;
  }

  function deriveFbc(params) {
    var existingFbc = getCookie('_fbc');
    if (existingFbc) return truncate(existingFbc, 300);

    var fbclid = paramValue(params, 'fbclid', 300);
    if (!fbclid) return '';

    return truncate('fb.1.' + Date.now() + '.' + fbclid, 350);
  }

  function buildContext(params, referrer, previousContext, includeAdIdentifiers) {
    var session = loadSession(params, referrer, includeAdIdentifiers);
    var fbp = includeAdIdentifiers ? (getCookie('_fbp') || previousContext.attribution_fbp || '') : '';
    var fbc = includeAdIdentifiers ? (getCookie('_fbc') || deriveFbc(params) || previousContext.attribution_fbc || '') : '';

    var context = {
      attribution_session_entry_url: session.attribution_session_entry_url || '',
      attribution_session_entry_path: session.attribution_session_entry_path || '',
      attribution_session_referrer: session.attribution_session_referrer || '',
      attribution_user_agent: truncate(window.navigator.userAgent || '', 350)
    };
    if (includeAdIdentifiers) {
      context.attribution_fbp = truncate(fbp, 300);
      context.attribution_fbc = truncate(fbc, 350);
    }
    return context;
  }

  function privacyAllowsAttribution() {
    var privacy = window.AnatomyCustomerPrivacy;
    return !!privacy
      && typeof privacy.marketingAllowed === 'function'
      && privacy.marketingAllowed() === true;
  }

  function privacyAllowsAdAttribution() {
    var privacy = window.AnatomyCustomerPrivacy;
    return !!privacy
      && typeof privacy.advertisingAllowed === 'function'
      && privacy.advertisingAllowed() === true;
  }

  function removeAdIdentifiers(state) {
    if (!state || typeof state !== 'object') return false;
    var changed = false;
    ['first', 'last'].forEach(function (prefix) {
      var touch = state[prefix];
      if (!touch || typeof touch !== 'object') return;
      for (var index = 0; index < CLICK_ID_KEYS.length; index += 1) {
        var field = prefix + '_' + CLICK_ID_KEYS[index];
        if (Object.prototype.hasOwnProperty.call(touch, field) && touch[field]) changed = true;
        delete touch[field];
      }
      var cleanUrl = safeUrl(touch[prefix + '_landing_url'], 700, false);
      var cleanPath = safePath(350, false, touch[prefix + '_landing_path']);
      if (touch[prefix + '_landing_url'] !== cleanUrl || touch[prefix + '_landing_path'] !== cleanPath) changed = true;
      touch[prefix + '_landing_url'] = cleanUrl;
      touch[prefix + '_landing_path'] = cleanPath;
    });
    if (state.context && typeof state.context === 'object') {
      ['attribution_fbp', 'attribution_fbc'].forEach(function (field) {
        if (state.context[field]) changed = true;
        delete state.context[field];
      });
      var entryUrl = safeUrl(state.context.attribution_session_entry_url, 700, false);
      var entryPath = safePath(350, false, state.context.attribution_session_entry_path);
      var entryReferrer = safeUrl(state.context.attribution_session_referrer, 500, false);
      if (state.context.attribution_session_entry_url !== entryUrl
        || state.context.attribution_session_entry_path !== entryPath
        || state.context.attribution_session_referrer !== entryReferrer) changed = true;
      state.context.attribution_session_entry_url = entryUrl;
      state.context.attribution_session_entry_path = entryPath;
      state.context.attribution_session_referrer = entryReferrer;
    }
    return changed;
  }

  function clearAttributionState() {
    currentState = null;
    safeStorageRemove(window.localStorage, STORAGE_KEY);
    safeStorageRemove(window.sessionStorage, SESSION_KEY);
    deleteCookie(COOKIE_FIRST);
    deleteCookie(COOKIE_LAST);
    deleteCookie(COOKIE_CONTEXT);
  }

  function cookieMaxLength(field) {
    if (field.indexOf('landing_url') !== -1 || field.indexOf('entry_url') !== -1) return 250;
    if (field.indexOf('landing_path') !== -1 || field.indexOf('entry_path') !== -1) return 200;
    if (field.indexOf('referrer') !== -1) return 250;
    if (field.indexOf('clid') !== -1 || field.indexOf('braid') !== -1 || field.indexOf('_fbc') !== -1 || field.indexOf('_fbp') !== -1) return 160;
    if (field.indexOf('user_agent') !== -1) return 220;
    return 120;
  }

  function compactForCookie(values, fields) {
    var compact = {};
    for (var index = 0; index < fields.length; index += 1) {
      var field = fields[index];
      if (values && values[field]) {
        compact[field] = truncate(values[field], cookieMaxLength(field));
      }
    }
    return compact;
  }

  function persistState(state) {
    safeStorageSet(window.localStorage, STORAGE_KEY, JSON.stringify(state));
    if (state.first) setCookie(COOKIE_FIRST, JSON.stringify(compactForCookie(state.first, FIRST_FIELDS)));
    if (state.last) setCookie(COOKIE_LAST, JSON.stringify(compactForCookie(state.last, LAST_FIELDS)));
    if (state.context) setCookie(COOKIE_CONTEXT, JSON.stringify(compactForCookie(state.context, CONTEXT_FIELDS)));
  }

  function refreshState() {
    if (!privacyAllowsAttribution()) return { state: null, changed: false, allowed: false };

    var params = currentParams();
    var includeAdIdentifiers = privacyAllowsAdAttribution();
    var referrer = externalReferrer(includeAdIdentifiers);
    var meaningful = hasMeaningfulSignal(params, referrer, includeAdIdentifiers);
    // Checkout, payment-callback and sign-in returns are re-entries, not
    // acquisition: a Shop Pay callback arrives with whichever merchant the
    // buyer used last as its referrer. They neither create nor change a touch.
    var reentry = channel.isReentry(window.location.href);
    var state = loadState();
    var changed = includeAdIdentifiers ? false : removeAdIdentifiers(state);
    var firstTouch = buildTouch('first', params, referrer, includeAdIdentifiers);
    var lastTouch = buildTouch('last', params, referrer, includeAdIdentifiers);

    state.version = VERSION;

    if (reentry) {
      // Leave first and last touch as they are.
    }
    else if (!state.first) {
      state.first = firstTouch;
      changed = true;
    }
    else if (fillMissing(state.first, firstTouch, FIRST_FIELDS)) {
      changed = true;
    }

    if (!reentry && (!state.last || meaningful)) {
      if (!state.last || !touchEquals(state.last, lastTouch, LAST_FIELDS)) {
        state.last = lastTouch;
        changed = true;
      }
    }

    var context = buildContext(params, referrer, state.context || {}, includeAdIdentifiers);
    if (JSON.stringify(context) !== JSON.stringify(state.context || {})) {
      state.context = context;
      changed = true;
    }

    currentState = state;
    if (changed) persistState(state);

    return { state: state, changed: changed, allowed: true, meaningful: meaningful, reentry: reentry };
  }

  function attributeKey(field) {
    return field.indexOf('attribution_') === 0 ? field : 'attribution_' + field;
  }

  function addAttribute(attributes, field, value) {
    var sanitized = truncate(value, 700);
    if (!sanitized) return;
    attributes[attributeKey(field)] = sanitized;
  }

  function cartAttributes(state) {
    var attributes = {};
    var index;

    if (state && state.first) {
      for (index = 0; index < FIRST_FIELDS.length; index += 1) {
        addAttribute(attributes, FIRST_FIELDS[index], state.first[FIRST_FIELDS[index]]);
      }
      var firstChannel = channel.classifyTouch(state.first, 'first');
      addAttribute(attributes, 'first_channel', firstChannel.channel);
      addAttribute(attributes, 'first_platform', firstChannel.platform);
    }

    if (state && state.last) {
      for (index = 0; index < LAST_FIELDS.length; index += 1) {
        addAttribute(attributes, LAST_FIELDS[index], state.last[LAST_FIELDS[index]]);
      }
      var lastChannel = channel.classifyTouch(state.last, 'last');
      addAttribute(attributes, 'last_channel', lastChannel.channel);
      addAttribute(attributes, 'last_platform', lastChannel.platform);
    }

    if (state && state.context) {
      for (index = 0; index < CONTEXT_FIELDS.length; index += 1) {
        addAttribute(attributes, CONTEXT_FIELDS[index], state.context[CONTEXT_FIELDS[index]]);
      }
    }

    return attributes;
  }

  function pushLpCheckoutEvent(form) {
    if (!form || !form.matches || !form.matches('[data-lp-checkout-form]')) return;
    if (!privacyAllowsAdAttribution()) return;

    var now = Date.now();
    if (now - lastLpCheckoutEventAt < 500) return;
    lastLpCheckoutEventAt = now;

    var button = form.querySelector('button[type="submit"], input[type="submit"]');
    var ctaText = button ? (button.innerText || button.value || '').replace(/\s+/g, ' ').trim() : '';
    var variantInput = form.querySelector('[name="id"]');
    var eventPayload = {
      event: 'lp_direct_checkout_clicked',
      lp_path: window.location.pathname,
      lp_url: window.location.href,
      cta_text: ctaText,
      variant_id: form.getAttribute('data-lp-variant-id') || (variantInput ? variantInput.value : '')
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventPayload);
  }

  function bindEvents() {
    // Cart-attribute sync (writing first/last-touch onto the cart) is owned by
    // anatomy-posthog.js, which syncs on /cart/add, /cart/change and checkout
    // click. This module only PERSISTS attribution state (cookies/storage) and
    // emits the GTM lp-checkout dataLayer event — it no longer writes the cart.
    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest
        ? event.target.closest('a, button, input[type="submit"], shopify-accelerated-checkout, shopify-accelerated-checkout-cart')
        : null;

      var lpCheckoutForm = target && target.closest
        ? target.closest('[data-lp-checkout-form]')
        : null;

      if (lpCheckoutForm) {
        pushLpCheckoutEvent(lpCheckoutForm);
      }
    }, true);

    document.addEventListener('submit', function (event) {
      pushLpCheckoutEvent(event.target);
    }, true);

  }

  function init() {
    repairStaleKlaviyoForm();
    bindEvents();

    window.anatomyAttribution = {
      getState: function () {
        if (!privacyAllowsAttribution()) return null;
        return JSON.parse(JSON.stringify(currentState || loadState()));
      },
      getCartAttributes: function () {
        if (!privacyAllowsAttribution()) return {};
        return cartAttributes(currentState || loadState());
      }
    };

    var privacy = window.AnatomyCustomerPrivacy;
    if (!privacy || typeof privacy.subscribe !== 'function') return;

    privacy.subscribe(function (snapshot) {
      if (privacyAllowsAttribution()) {
        refreshState();
        if (DEBUG && window.console && window.console.info) {
          window.console.info('[ANATOMY attribution] initialized', {
            state: currentState,
            cart_attributes: cartAttributes(currentState || {})
          });
        }
        return;
      }

      if (snapshot && (snapshot.status === 'ready' || snapshot.status === 'error')) {
        clearAttributionState();
      }
    });
  }

  init();
})();
